import { createSlice } from '@reduxjs/toolkit';
import {
  fetchExtensionScripts,
  fetchExtensionScript,
  createExtensionScript,
  updateExtensionScript,
  deleteExtensionScript,
  fetchVersionHistory,
  revertScript,
  exportAllScripts,
  fetchExecutionHistory,
} from '../thunks/extensionScriptsThunks';

const initialState = {
  scripts: [],
  currentScript: null,
  versionHistory: [],
  executionHistory: {}, // { [scriptId]: execution[] }
  exportData: null,
  loading: false,
  error: null,
};

const extensionScriptsSlice = createSlice({
  name: 'extensionScripts',
  initialState,
  reducers: {
    clearCurrentScript: (state) => {
      state.currentScript = null;
      state.versionHistory = [];
    },
    clearExportData: (state) => {
      state.exportData = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchExtensionScripts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchExtensionScripts.fulfilled, (state, action) => {
      state.loading = false;
      state.scripts = action.payload;
    });
    builder.addCase(fetchExtensionScripts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Fetch one
    builder.addCase(fetchExtensionScript.fulfilled, (state, action) => {
      state.currentScript = action.payload;
    });

    // Create
    builder.addCase(createExtensionScript.fulfilled, (state, action) => {
      state.scripts.push(action.payload);
      state.currentScript = action.payload;
    });

    // Update
    builder.addCase(updateExtensionScript.fulfilled, (state, action) => {
      const idx = state.scripts.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.scripts[idx] = action.payload;
      state.currentScript = action.payload;
    });

    // Delete
    builder.addCase(deleteExtensionScript.fulfilled, (state, action) => {
      state.scripts = state.scripts.filter((s) => s.id !== action.payload);
      if (state.currentScript?.id === action.payload) {
        state.currentScript = null;
      }
    });

    // Version history
    builder.addCase(fetchVersionHistory.fulfilled, (state, action) => {
      state.versionHistory = action.payload;
    });

    // Revert
    builder.addCase(revertScript.fulfilled, (state, action) => {
      const idx = state.scripts.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) state.scripts[idx] = action.payload;
      state.currentScript = action.payload;
    });

    // Export
    builder.addCase(exportAllScripts.fulfilled, (state, action) => {
      state.exportData = action.payload;
    });

    // Execution history
    builder.addCase(fetchExecutionHistory.fulfilled, (state, action) => {
      const { scriptId, executions } = action.payload;
      state.executionHistory[scriptId] = executions;
    });
  },
});

export const { clearCurrentScript, clearExportData, clearError } = extensionScriptsSlice.actions;
export default extensionScriptsSlice.reducer;
